import React from 'react';
import { Link } from 'react-router-dom';

const PLANS = [
  {
    name: 'Starter',
    icon: '🌱',
    desc: 'Perfect for small clubs and one-off events.',
    features: [
      '3 tournaments / month',
      'Up to 32 players per tournament',
      'Knockout & League draws',
      'Public tournament page',
      'Email notifications',
    ],
    highlight: false,
  },
  {
    name: 'Pro',
    icon: '⚡',
    desc: 'For active clubs running regular tournaments.',
    features: [
      'Unlimited tournaments',
      'Up to 256 players per tournament',
      'All draw types incl. Double Elimination',
      'Staff roles & delegation',
      'Custom branding & domain',
      'Priority support',
      'Advanced analytics',
    ],
    highlight: true,
  },
  {
    name: 'Federation',
    icon: '🏛️',
    desc: 'For state & national bodies managing large-scale events.',
    features: [
      'Everything in Pro',
      'Multi-tournament series',
      'Rankings & points system',
      'Bulk player import & profile linking',
      'API access',
      'Dedicated account manager',
      'SLA support',
    ],
    highlight: false,
  },
];

const UPCOMING = [
  { icon: '🏆', title: 'Tournament Series', desc: 'Link multiple tournaments into a season with cumulative points and live rankings.' },
  { icon: '📊', title: 'National Rankings', desc: 'Automatic rating updates after every match using an ELO-style algorithm.' },
  { icon: '💳', title: 'Online Payments', desc: 'Collect registration fees securely. Auto-reconcile with player profiles.' },
  { icon: '🖥️', title: 'Desktop App', desc: 'Native Windows & macOS app for full offline tournament management — no internet required on match day.' },
  { icon: '📱', title: 'Mobile App', desc: 'Dedicated iOS & Android apps for players, organisers, and spectators.' },
  { icon: '🎙️', title: 'Live Scoring', desc: 'Real-time score updates pushed to spectators and the public tournament page.' },
  { icon: '🤖', title: 'AI Draw Assistant', desc: 'Smart draw generation that avoids same-club clashes in early rounds.' },
];

const SubscriptionPage = () => (
  <div style={{ paddingBottom: '4rem' }}>

    {/* ── Hero ──────────────────────────────────────────────────────────── */}
    <div style={{
      background: 'linear-gradient(135deg, #0f172a 0%, #134E4A 50%, #0F766E 100%)',
      padding: '3.5rem 1rem',
      textAlign: 'center',
      position: 'relative',
      overflow: 'hidden',
    }}>
      <div style={{ position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(circle at 20% 50%, rgba(13,148,136,0.15) 0%, transparent 60%), radial-gradient(circle at 80% 20%, rgba(124,58,237,0.12) 0%, transparent 50%)', pointerEvents: 'none' }} />
      <div style={{ position: 'relative', zIndex: 1 }}>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 999, padding: '4px 14px', fontSize: '0.72rem', color: '#fbbf24', marginBottom: '1rem', letterSpacing: '0.08em', textTransform: 'uppercase', fontWeight: 700 }}>
          🚧 Coming Soon
        </div>
        <h1 style={{ color: 'white', fontSize: 'clamp(1.8rem, 4vw, 2.6rem)', fontWeight: 800, margin: '0 0 0.75rem', lineHeight: 1.15 }}>
          Simple Pricing.<br />Powerful Features.
        </h1>
        <p style={{ color: '#94a3b8', fontSize: '1rem', maxWidth: 460, margin: '0 auto', lineHeight: 1.7 }}>
          We're putting the finishing touches on our subscription plans. Leave your email and we'll notify you the moment they go live.
        </p>
      </div>
    </div>

    {/* ── Pricing cards ─────────────────────────────────────────────────── */}
    <div style={{ maxWidth: 960, margin: '2.5rem auto 0', padding: '0 1rem' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '1.25rem', alignItems: 'start' }}>
        {PLANS.map(plan => (
          <div
            key={plan.name}
            style={{
              background: plan.highlight ? 'linear-gradient(160deg, #0D9488 0%, #0F766E 100%)' : 'white',
              border: plan.highlight ? 'none' : '1px solid #f0f0f0',
              borderRadius: 16,
              padding: '1.75rem',
              boxShadow: plan.highlight ? '0 20px 48px rgba(13,148,136,0.3)' : '0 2px 8px rgba(0,0,0,0.06)',
              transform: plan.highlight ? 'scale(1.03)' : 'none',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            {plan.highlight && (
              <div style={{ position: 'absolute', top: 14, right: 14, background: '#fbbf24', color: '#78350f', fontSize: '0.62rem', fontWeight: 800, padding: '2px 9px', borderRadius: 999, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                Most Popular
              </div>
            )}
            <div style={{ fontSize: '2rem', marginBottom: '0.75rem' }}>{plan.icon}</div>
            <div style={{ fontWeight: 800, fontSize: '1.1rem', color: plan.highlight ? 'white' : '#111827', marginBottom: '0.25rem' }}>{plan.name}</div>
            <div style={{ marginBottom: '0.5rem' }}>
              <span style={{ fontSize: '2rem', fontWeight: 800, color: plan.highlight ? 'white' : '#111827' }}>{plan.price}</span>
              {plan.period && <span style={{ fontSize: '0.85rem', color: plan.highlight ? 'rgba(255,255,255,0.7)' : '#9ca3af', marginLeft: '4px' }}>{plan.period}</span>}
            </div>
            <p style={{ fontSize: '0.82rem', color: plan.highlight ? 'rgba(255,255,255,0.75)' : '#6b7280', marginBottom: '1.25rem', lineHeight: 1.6 }}>{plan.desc}</p>

            <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 1.5rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {plan.features.map(f => (
                <li key={f} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', fontSize: '0.82rem', color: plan.highlight ? 'rgba(255,255,255,0.85)' : '#374151' }}>
                  <span style={{ width: 16, height: 16, borderRadius: '50%', background: plan.highlight ? 'rgba(255,255,255,0.2)' : '#F0FDFA', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '1px' }}>
                    <svg width="8" height="8" viewBox="0 0 10 8" fill="none"><path d="M1 4l3 3 5-6" stroke={plan.highlight ? 'white' : '#0D9488'} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </span>
                  {f}
                </li>
              ))}
            </ul>

            <button
              style={{
                width: '100%', padding: '0.65rem', borderRadius: 10, fontWeight: 700,
                fontSize: '0.875rem', cursor: 'not-allowed',
                background: plan.highlight ? 'white' : '#f3f4f6',
                color: plan.highlight ? '#0D9488' : '#9ca3af',
                border: 'none',
                opacity: plan.name === 'Starter' ? 0.6 : 1,
              }}
              disabled
            >
              {plan.cta} — Coming Soon
            </button>
          </div>
        ))}
      </div>
    </div>

    {/* ── What's coming ─────────────────────────────────────────────────── */}
    <div style={{ maxWidth: 960, margin: '3.5rem auto 0', padding: '0 1rem' }}>
      <div style={{ textAlign: 'center', marginBottom: '1.75rem' }}>
        <h2 style={{ fontSize: '1.35rem', fontWeight: 800, color: '#111827', margin: '0 0 0.4rem' }}>On the Roadmap</h2>
        <p style={{ color: '#9ca3af', fontSize: '0.85rem' }}>Features we're actively building — available in Pro and Federation tiers.</p>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '1rem' }}>
        {UPCOMING.map(item => (
          <div key={item.title} style={{ background: 'white', border: '1px solid #f0f0f0', borderRadius: 12, padding: '1.1rem 1.25rem', display: 'flex', gap: '0.85rem', alignItems: 'flex-start', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
            <span style={{ fontSize: '1.5rem', flexShrink: 0, lineHeight: 1 }}>{item.icon}</span>
            <div>
              <div style={{ fontWeight: 700, fontSize: '0.88rem', color: '#111827', marginBottom: '0.25rem' }}>{item.title}</div>
              <div style={{ fontSize: '0.78rem', color: '#9ca3af', lineHeight: 1.6 }}>{item.desc}</div>
            </div>
          </div>
        ))}
      </div>
    </div>

    {/* ── CTA ───────────────────────────────────────────────────────────── */}
    <div style={{ maxWidth: 560, margin: '3rem auto 0', padding: '0 1rem', textAlign: 'center' }}>
      <div style={{ background: '#f0fdfa', border: '1px solid #CCFBF1', borderRadius: 16, padding: '2rem' }}>
        <h3 style={{ margin: '0 0 0.5rem', fontWeight: 800, fontSize: '1.1rem', color: '#111827' }}>Be the first to know</h3>
        <p style={{ color: '#6b7280', fontSize: '0.85rem', marginBottom: '1.25rem', lineHeight: 1.6 }}>
          We'll send you a one-time email when subscriptions launch. No spam, ever.
        </p>
        <div style={{ display: 'flex', gap: '0.5rem', maxWidth: 360, margin: '0 auto' }}>
          <input
            type="email"
            placeholder="your@email.com"
            style={{ flex: 1, height: 40, padding: '0 12px', fontSize: '0.875rem', border: '1px solid #CCFBF1', borderRadius: 8, outline: 'none', fontFamily: 'inherit', color: '#111827' }}
          />
          <button
            style={{ height: 40, padding: '0 18px', background: '#0D9488', color: 'white', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: '0.875rem', cursor: 'pointer', whiteSpace: 'nowrap' }}
            onClick={() => alert('Thanks! We\'ll notify you when subscriptions launch.')}
          >
            Notify Me
          </button>
        </div>
        <p style={{ fontSize: '0.7rem', color: '#99F6E4', marginTop: '0.75rem' }}>
          Already using the free tier?{' '}
          <Link to="/tournaments" style={{ color: '#0D9488', fontWeight: 600, textDecoration: 'none' }}>Browse tournaments →</Link>
        </p>
      </div>
    </div>

  </div>
);

export default SubscriptionPage;
